/**
 * Shared visual tokens for report preview (HTML) and DOCX export.
 * Values mirror previewTheme.js + NodeRenderer/ListRenderer spacing.
 *
 * px → twips: px / 96 * 1440  (at 96dpi)
 * rem → px: rem * 16 (base font size 16px in previewTheme)
 */

const pxToTwips = (px) => Math.round((px / 96) * 1440);
const remToTwips = (rem) => pxToTwips(rem * 16);

export const PREVIEW_FONT = 'Georgia';

export const PREVIEW_COLORS = {
    title: '17365D',
    headingPrimary: '17365D',
    headingSecondary: '365F91',
    link: '1976D2',
    metaBackground: 'F9F9F9',
};

/** docx TextRun size is half-points; px * 0.75 ≈ pt (96/72) */
export const PREVIEW_FONT_SIZE = {
    bodyHalfPoints: 24,
    titleHalfPoints: 48,
    headingLevel1HalfPoints: 48,
    headingLevel2HalfPoints: 42,
    headingLevel3HalfPoints: 27,
};

export const PREVIEW_SPACING = {
    /** Typography paragraph mb:2 in NodeRenderer (16px) */
    paragraphAfter: pxToTwips(16),
    /** body1 lineHeight 1.75 on 16px */
    paragraphLine: remToTwips(1.75),
    /** h4 marginTop 2rem / marginBottom 0.75rem */
    heading1Before: remToTwips(2),
    heading1After: remToTwips(0.75),
    /** h5 marginTop 1.5rem / marginBottom 0.5rem */
    heading2Before: remToTwips(1.5),
    heading2After: remToTwips(0.5),
    /** h6 marginTop 1.5rem / marginBottom 0.5rem */
    heading3Before: remToTwips(1.5),
    heading3After: remToTwips(0.5),
    /** .preview-root ol/ul marginBlock 0.75rem */
    listBlockMargin: remToTwips(0.75),
    /** .preview-root li marginBlockEnd 0.375rem */
    listItemAfter: remToTwips(0.375),
    /** .preview-root ul paddingInlineStart 1.5rem */
    listIndent: remToTwips(1.5),
    listHanging: pxToTwips(10),
    /** ReportMetadata box padding p={2} pt={1} */
    metaPaddingTop: remToTwips(0.5),
    metaPaddingSide: remToTwips(1),
    metaPaddingBottom: remToTwips(1),
    /** ReportContent title → metadata gap */
    titleAfter: remToTwips(1),
    /** ReportMetadata mb={2} */
    metaBlockAfter: remToTwips(1),
    /** MUI Typography gutterBottom on headings (0.35em of heading font size) */
    headingGutterAfter: {
        1: pxToTwips(32 * 0.35),
        2: pxToTwips(28 * 0.35),
        3: pxToTwips(18 * 0.35),
    },
    /** Gap between top-level judgment blocks (nested ul bottom + li margin) */
    judgmentBlockGap: remToTwips(0.75) + remToTwips(0.375),
    /** Gap between judgment line and nested Confidence/Justification list */
    nestedListGap: pxToTwips(16) + remToTwips(0.75),
    /** Extra space after a section heading when a body paragraph follows (e.g. PURPOSE / SCOPE) */
    headingAfterParagraph: pxToTwips(16) + remToTwips(0.75),
    /** Two Enter presses before a section heading that follows body text (e.g. KEY JUDGMENTS) */
    headingBeforeParagraphGap: pxToTwips(16) * 2,
};

export const PREVIEW_PAGE = {
    width: 12240,
    height: 15840,
    margin: 1440,
    contentWidth: 12240 - (1440 * 2),
};
